// Asset / exploit lifecycle system.
//
// Every searchable hotspot wears a blue '?' from game start — the player
// doesn't know which ones contain assets and which are empty. config.assets.
// count assets are placed in random hotspots; the rest are red herrings.
//
// Player loop:
//   1. Search a '?' hotspot
//      - asset    → status='carrying' (sprite + 'CARRYING / [name]' callout
//                    track the player; carry is sacred — never wiped)
//      - empty    → status='searched' (brief 'EMPTY' tag, '?' gone)
//   2. Walk to scanner → drop → status='scanning' for scanMs (beam +
//      'AT SCANNER / SCANNING…' callout)
//   3. Scan reveals: roll exploitableRate
//      - exploitable → status='exposed' (asset stays at scanner, 'EXPOSED /
//                       [name]' tooltip, PATCH action available there)
//      - safe        → status='dismissed' (brief 'NOT EXPLOITABLE', asset gone)
//   4. Player presses PATCH near the scanner → status='patched' (HUD chip
//      fills with the asset icon)
//
// Defensive loop (CVEs bouncing into hotspots):
//   - CVE collides with a hotspot in 'hidden' status that holds an asset →
//     status='exploiting' (yellow tint, 'EXPLOITING' bar, 5s timer; asset
//     icon is revealed at the hotspot for the duration)
//   - PATCH near the hotspot during the window → status='patched'
//   - Timer expires → status='exploited' (-1 hp, asset gone)
//
// Round reset (every roundResetSec, fully silent):
//   - 'searched' and 'dismissed' hotspots flip back to 'hidden' (? returns)
//   - assets are re-randomised across all hotspots in 'hidden' status so
//     the world maintains config.assets.count active assets
//   - 'carrying', 'scanning', 'exposed', 'exploiting', 'patched',
//     'exploited' are untouched

import Phaser from 'phaser';
import { brand, brandHex } from '../brand';
import { config, AssetType } from '../config';
import { SPRITE_KEYS } from './sprites';
import { SearchHotspot } from './arena';
import { AUDIO_KEYS, playSfx } from './audio';
import { makeCallout } from './callout';

// Friendly names shown in "Asset Found — X" callouts and HUD chips.
const ASSET_LABEL: Record<AssetType, string> = {
  webserver: 'Webserver',
  domain: 'Domain',
  s3: 'S3 Bucket',
  api: 'API Endpoint',
  database: 'Database',
  cloud: 'Cloud Environment',
  cert: 'Cert',
  subdomain: 'Subdomain',
  email: 'Email Server',
  dns: 'Dangling DNS',
  dataleak: 'Data Storage',
};

// Exploit headlines per GAME_LOGIC.md. Shown in the EXPOSED tooltip when
// the scanner flags an asset as exploitable.
const EXPOSED_LABEL: Record<AssetType, string> = {
  webserver: 'Access Escalation',
  domain: 'Hijackable Domain',
  s3: 'Insecure Access',
  api: 'Auth Bypass',
  database: 'Data Readable',
  cloud: 'Exposed Credentials',
  cert: 'Misconfiguration',
  subdomain: 'Hijackable Domain',
  email: 'Email Hijack',
  dns: 'Hijackable Domain',
  dataleak: 'Access Exploit',
};

const labelFor = (type: AssetType, exposed = false): string =>
  (exposed && EXPOSED_LABEL[type]) || ASSET_LABEL[type];

export type HotspotStatus =
  | 'hidden'        // '?' marker visible, may or may not contain an asset
  | 'searched'      // search revealed empty, '?' gone, awaiting silent reset
  | 'carrying'      // on the player
  | 'scanning'      // at scanner, processing
  | 'exposed'       // scanner flagged exploitable, awaiting patch at scanner
  | 'dismissed'     // scanner flagged safe, asset gone, awaiting silent reset
  | 'exploiting'    // CVE hit a hidden+asset hotspot, 5s exploit timer
  | 'patched'       // terminal — counts toward boss trigger
  | 'exploited';    // terminal — cost the hero a health segment

export interface AssetCallbacks {
  onSearch: (kind: SearchHotspot['kind']) => void;
  /** Fired only for the proper scanner-route patch — fills the HUD chip. */
  onPatch: (type: AssetType, slotIndex: number) => void;
  onExploited: (type: AssetType) => void;
  onAllPatched: () => void;
  onCarryStart: () => void;
  onCarryEnd: () => void;
  /** A CVE just kicked off an exploit timer on a hotspot. */
  onExploitStart: () => void;
  /** That exploit timer resolved (in-place patch, or timer expired). */
  onExploitEnd: () => void;
}

interface HotspotState {
  hotspot: SearchHotspot;
  status: HotspotStatus;
  /** The asset hidden inside, or null if this hotspot is currently empty. */
  type: AssetType | null;
  marker: Phaser.GameObjects.Container | null;
  /** Asset icon. Lives above player while carrying, on scanner deck while
   *  scanning/exposed, at hotspot while exploiting. Cleared otherwise. */
  assetSprite: Phaser.GameObjects.Sprite | null;
  carryCallout: Phaser.GameObjects.Container | null;
  scanCallout: Phaser.GameObjects.Container | null;
  exposedCallout: Phaser.GameObjects.Container | null;
  scanBeam: Phaser.GameObjects.Rectangle | null;
  scanTween: Phaser.Tweens.Tween | null;
  scanTimer: Phaser.Time.TimerEvent | null;
  exploitBar: Phaser.GameObjects.Graphics | null;
  exploitBarFill: Phaser.GameObjects.Graphics | null;
  exploitLabel: Phaser.GameObjects.Text | null;
  exploitTimer: Phaser.Time.TimerEvent | null;
  exploitStartMs: number;
}

const BAR_W = 36;
const BAR_H = 4;
/** Auto-drop a carried asset within this many px of the scanner centre. */
const SCANNER_DROP_RADIUS = 50;
/** PATCH action becomes available within this many px of the scanner. */
const SCANNER_PATCH_RADIUS = 60;

export class AssetSystem {
  private scene: Phaser.Scene;
  private cbs: AssetCallbacks;
  private states: HotspotState[] = [];
  private patchedCount = 0;
  private scannerPos: { x: number; y: number } = { x: 0, y: 0 };

  constructor(scene: Phaser.Scene, _bounds: Phaser.Geom.Rectangle, cbs: AssetCallbacks) {
    this.scene = scene;
    this.cbs = cbs;
  }

  /**
   * Initialise per-hotspot state. Every hotspot starts as 'hidden' with a
   * '?' marker; reshuffle() then distributes config.assets.count assets
   * randomly across them.
   */
  init(hotspots: SearchHotspot[], scanner: { x: number; y: number }): void {
    this.scannerPos = scanner;
    this.states = hotspots.map((h) => ({
      hotspot: h,
      status: 'hidden',
      type: null,
      marker: null,
      assetSprite: null,
      carryCallout: null,
      scanCallout: null,
      exposedCallout: null,
      scanBeam: null,
      scanTween: null,
      scanTimer: null,
      exploitBar: null,
      exploitBarFill: null,
      exploitLabel: null,
      exploitTimer: null,
      exploitStartMs: 0,
    }));

    // '?' on every hotspot from frame zero
    for (const s of this.states) s.marker = this.makeMarker(s.hotspot.x, s.hotspot.y - 28);

    this.reshuffleAssets();
  }

  /** True iff the player is currently carrying. Used for action gating. */
  isCarrying(): boolean {
    return this.states.some((s) => s.status === 'carrying');
  }

  /**
   * Silent reshuffle. Called periodically by GameScene's round timer. Any
   * 'searched' or 'dismissed' hotspot flips back to 'hidden' and gets a
   * fresh '?'; the asset pool is re-randomised across all 'hidden'
   * hotspots so the world maintains exactly config.assets.count active
   * assets at any time.
   */
  startRound(_count: number): void {
    for (const s of this.states) {
      if (s.status === 'searched' || s.status === 'dismissed') {
        s.status = 'hidden';
        s.type = null;
        if (!s.marker) s.marker = this.makeMarker(s.hotspot.x, s.hotspot.y - 28);
      }
    }
    this.reshuffleAssets();
  }

  /**
   * Distribute config.assets.count asset types randomly across all currently
   * 'hidden' hotspots. The rest stay empty (red herrings). Any in-flight
   * status (carrying/scanning/etc.) is left alone.
   */
  private reshuffleAssets(): void {
    const types = config.assets.types;
    const candidates = this.states.filter((s) => s.status === 'hidden');
    // Fisher–Yates shuffle
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Phaser.Math.Between(0, i);
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }
    candidates.forEach((s, i) => {
      if (i < config.assets.count) {
        s.type = types[Phaser.Math.Between(0, types.length - 1)] as AssetType;
      } else {
        s.type = null;
      }
    });
  }

  /** Closest hidden hotspot in search range — drives the SEARCH action. */
  getNearbyHidden(player: Phaser.Physics.Arcade.Sprite): HotspotState | null {
    return this.findNearby(player, 'hidden', config.assets.searchRadius);
  }

  /** Closest exploiting hotspot in patch range. */
  getNearbyExploiting(player: Phaser.Physics.Arcade.Sprite): HotspotState | null {
    return this.findNearby(player, 'exploiting', config.assets.searchRadius);
  }

  /** Are we near the scanner with an asset waiting to be patched there? */
  getExposedAtScanner(player: Phaser.Physics.Arcade.Sprite): HotspotState | null {
    const exposed = this.states.find((s) => s.status === 'exposed');
    if (!exposed) return null;
    const d = Phaser.Math.Distance.Between(player.x, player.y, this.scannerPos.x, this.scannerPos.y);
    return d <= SCANNER_PATCH_RADIUS ? exposed : null;
  }

  private findNearby(
    player: Phaser.Physics.Arcade.Sprite,
    status: HotspotStatus,
    radius: number,
  ): HotspotState | null {
    let best: HotspotState | null = null;
    let bestD = Infinity;
    for (const s of this.states) {
      if (s.status !== status) continue;
      const d = Phaser.Math.Distance.Between(player.x, player.y, s.hotspot.x, s.hotspot.y);
      if (d < radius && d < bestD) {
        bestD = d;
        best = s;
      }
    }
    return best;
  }

  /**
   * Search the nearest hidden hotspot. If it has an asset, the hero picks
   * it up (status='carrying'); if not, the hotspot goes to 'searched' and
   * waits for a silent reset.
   */
  trySearch(player: Phaser.Physics.Arcade.Sprite): boolean {
    if (this.isCarrying()) return false; // one at a time
    const target = this.getNearbyHidden(player);
    if (!target) return false;

    // marker animates out either way
    if (target.marker) {
      const m = target.marker;
      this.scene.tweens.add({
        targets: m,
        alpha: 0,
        scale: 0.3,
        duration: 220,
        onComplete: () => m.destroy(),
      });
      target.marker = null;
    }

    if (target.type === null) {
      // empty — brief 'EMPTY' tag, hotspot becomes 'searched'
      playSfx(this.scene, AUDIO_KEYS.sfxSearchEmpty);
      const tag = this.scene.add.text(target.hotspot.x, target.hotspot.y - 18, 'EMPTY', {
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '9px',
        fontStyle: '700',
        color: brand.textDim,
      }).setOrigin(0.5).setDepth(20);
      this.scene.tweens.add({
        targets: tag,
        y: tag.y - 16,
        alpha: 0,
        duration: 700,
        onComplete: () => tag.destroy(),
      });
      target.status = 'searched';
      this.cbs.onSearch(target.hotspot.kind);
      return true;
    }

    // asset found — pick it up
    playSfx(this.scene, AUDIO_KEYS.sfxSearchFound);

    this.scene.add.particles(target.hotspot.x, target.hotspot.y, SPRITE_KEYS.pixel, {
      lifespan: 400,
      speed: { min: 60, max: 140 },
      quantity: 12,
      tint: brandHex.asset,
      alpha: { start: 1, end: 0 },
      scale: { start: 3, end: 0 },
      blendMode: 'ADD',
      emitting: false,
    }).explode(12);

    // 'ASSET FOUND / [name]' callout drifts up at the hotspot.
    const found = makeCallout(this.scene, target.hotspot.x, target.hotspot.y - 18, {
      kind: 'found',
      label: ASSET_LABEL[target.type],
      iconKey: SPRITE_KEYS.asset[target.type],
    });
    this.scene.tweens.add({
      targets: found,
      y: found.y - 18,
      alpha: 0,
      duration: 1100,
      delay: 350,
      onComplete: () => found.destroy(),
    });

    // Asset sprite + 'CARRYING' callout pinned above the player. Sacred —
    // not wiped on round reset.
    target.assetSprite = this.scene.add.sprite(
      player.x,
      player.y - 28,
      SPRITE_KEYS.asset[target.type],
    ).setOrigin(0.5).setDepth(20).setDisplaySize(22, 22);

    target.carryCallout = makeCallout(this.scene, player.x, player.y - 32, {
      kind: 'carrying',
      label: ASSET_LABEL[target.type],
    });

    target.status = 'carrying';
    this.cbs.onSearch(target.hotspot.kind);
    this.cbs.onCarryStart();
    return true;
  }

  /**
   * PATCH action. Two flavours:
   *   - exposed asset at scanner    → completePatch(): counts toward boss
   *     trigger, fills the HUD chip, terminal 'patched' status.
   *   - exploiting hotspot in range → defendInPlace(): cancels the CVE
   *     timer and reverts to 'hidden' (asset still there, '?' returns).
   *     Doesn't count toward the patched counter.
   */
  tryPatch(player: Phaser.Physics.Arcade.Sprite): boolean {
    const exposed = this.getExposedAtScanner(player);
    if (exposed) {
      this.completePatch(exposed, this.scannerPos);
      return true;
    }
    const exploiting = this.getNearbyExploiting(player);
    if (exploiting) {
      this.defendInPlace(exploiting);
      return true;
    }
    return false;
  }

  /**
   * CVE-vs-item collision. If the hotspot is in 'hidden' AND has an asset,
   * the CVE rips off the cover and a 5-second exploit timer starts. Empty
   * hidden hotspots and any other state just bounce.
   */
  handleCveHit(item: Phaser.GameObjects.GameObject): void {
    const hs = item.getData('hotspot') as SearchHotspot | undefined;
    if (!hs) return;
    const state = this.states.find((s) => s.hotspot === hs);
    if (!state || state.status !== 'hidden' || !state.type) return;

    // marker out, asset visible at hotspot for the timer's duration
    if (state.marker) {
      const m = state.marker;
      this.scene.tweens.add({
        targets: m, alpha: 0, scale: 0.3, duration: 220,
        onComplete: () => m.destroy(),
      });
      state.marker = null;
    }
    state.assetSprite = this.scene.add.sprite(
      state.hotspot.x, state.hotspot.y - 22,
      SPRITE_KEYS.asset[state.type],
    ).setOrigin(0.5).setDepth(20).setDisplaySize(22, 22);

    state.status = 'exploiting';
    state.exploitStartMs = this.scene.time.now;
    state.hotspot.body.setTint(brandHex.asset);
    this.cbs.onExploitStart();

    const barY = state.hotspot.y - 36;
    const barX = state.hotspot.x - BAR_W / 2;
    state.exploitBar = this.scene.add.graphics().setDepth(21);
    state.exploitBar.fillStyle(0x000000, 0.45);
    state.exploitBar.fillRect(barX, barY, BAR_W, BAR_H);
    state.exploitBarFill = this.scene.add.graphics().setDepth(22);
    state.exploitLabel = this.scene.add.text(state.hotspot.x, barY - 9, 'EXPLOITING', {
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '8px',
      fontStyle: '700',
      color: brand.warn,
    }).setOrigin(0.5).setDepth(22);

    // Visual-only warning — the red edge glow on #exploit-warning replaces
    // the old sfxScanExploitable alarm here. Scanner-route reveals still
    // play the SFX (revealScanResult).

    state.exploitTimer = this.scene.time.delayedCall(
      config.assets.exploitDurationMs,
      () => this.markExploited(state),
    );
  }

  /** Per-frame tick: pin carry visuals, run scanner drop-off, draw exploit bar. */
  update(player: Phaser.Physics.Arcade.Sprite): void {
    const now = this.scene.time.now;
    const scannerBusy = this.states.some(
      (s) => s.status === 'scanning' || s.status === 'exposed',
    );
    for (const s of this.states) {
      // Carry: pin asset + callout above the player; drop at the scanner if free.
      if (s.status === 'carrying') {
        s.assetSprite?.setPosition(player.x, player.y - 28);
        s.carryCallout?.setPosition(player.x, player.y - 32);
        if (!scannerBusy) {
          const d = Phaser.Math.Distance.Between(
            player.x, player.y, this.scannerPos.x, this.scannerPos.y,
          );
          if (d <= SCANNER_DROP_RADIUS) this.dropAtScanner(s);
        }
      }

      // Exploit bar fill.
      if (s.status === 'exploiting' && s.exploitBarFill) {
        const progress = Math.min(1, (now - s.exploitStartMs) / config.assets.exploitDurationMs);
        const barY = s.hotspot.y - 36;
        const barX = s.hotspot.x - BAR_W / 2;
        s.exploitBarFill.clear();
        s.exploitBarFill.fillStyle(brandHex.threat, 1);
        s.exploitBarFill.fillRect(barX, barY, Math.round(BAR_W * progress), BAR_H);
      }
    }
  }

  getPatchedCount(): number {
    return this.patchedCount;
  }

  destroy(): void {
    for (const s of this.states) this.toBlank(s);
    this.states = [];
  }

  // ----- internals --------------------------------------------------------

  /**
   * Asset reaches the scanner. Asset settles on the deck, beam sweeps,
   * 'AT SCANNER / SCANNING…' callout shows. After scanMs the scanner
   * decides exploitable or safe.
   */
  private dropAtScanner(s: HotspotState): void {
    if (!s.type) return;
    playSfx(this.scene, AUDIO_KEYS.sfxScanStart);

    const settleY = this.scannerPos.y - 30;
    if (s.assetSprite) {
      const sprite = s.assetSprite;
      this.scene.tweens.add({
        targets: sprite,
        x: this.scannerPos.x,
        y: settleY,
        duration: 220,
        ease: 'Quad.Out',
      });
    }
    if (s.carryCallout) {
      const c = s.carryCallout;
      this.scene.tweens.add({
        targets: c,
        alpha: 0,
        duration: 160,
        onComplete: () => c.destroy(),
      });
      s.carryCallout = null;
    }

    s.scanCallout = makeCallout(
      this.scene,
      this.scannerPos.x,
      this.scannerPos.y - 56,
      { kind: 'info', eyebrow: 'AT SCANNER', label: 'SCANNING…' },
    );

    s.scanBeam = this.scene.add.rectangle(
      this.scannerPos.x, this.scannerPos.y - 30,
      72, 2, brandHex.primary, 0.9,
    ).setDepth(15);
    s.scanTween = this.scene.tweens.add({
      targets: s.scanBeam,
      y: this.scannerPos.y + 6,
      yoyo: true,
      repeat: -1,
      duration: 320,
      ease: 'Sine.InOut',
    });

    s.status = 'scanning';
    this.cbs.onCarryEnd();

    s.scanTimer = this.scene.time.delayedCall(config.assets.scanMs, () => this.revealScanResult(s));
  }

  /**
   * Scanner finished. Roll exploitableRate — exploitable assets show the
   * 'EXPOSED' tooltip and wait for PATCH; safe ones evaporate quietly.
   */
  private revealScanResult(s: HotspotState): void {
    if (s.status !== 'scanning' || !s.type) return;

    // tear down scan-only visuals
    s.scanTween?.stop();
    s.scanTween = null;
    s.scanBeam?.destroy();
    s.scanBeam = null;
    if (s.scanCallout) {
      const c = s.scanCallout;
      this.scene.tweens.add({
        targets: c, alpha: 0, duration: 180,
        onComplete: () => c.destroy(),
      });
      s.scanCallout = null;
    }
    s.scanTimer = null;

    if (Math.random() < config.assets.exploitableRate) {
      // EXPOSED — asset stays on the scanner deck, awaiting PATCH
      playSfx(this.scene, AUDIO_KEYS.sfxScanExploitable);
      s.exposedCallout = makeCallout(
        this.scene,
        this.scannerPos.x,
        this.scannerPos.y - 56,
        {
          kind: 'exposed',
          label: labelFor(s.type, true),
          iconKey: SPRITE_KEYS.asset[s.type],
        },
      );
      this.scene.tweens.add({
        targets: s.exposedCallout,
        alpha: { from: 0.6, to: 1 },
        yoyo: true,
        repeat: -1,
        duration: 700,
      });
      s.status = 'exposed';
    } else {
      // safe — quick 'SAFE' flash, asset fades, hotspot waits for reset
      playSfx(this.scene, AUDIO_KEYS.sfxScanSafe);
      const tag = this.scene.add.text(this.scannerPos.x, this.scannerPos.y - 56, 'NOT EXPLOITABLE', {
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '9px',
        fontStyle: '700',
        color: brand.primary,
      }).setOrigin(0.5).setDepth(20);
      this.scene.tweens.add({
        targets: tag,
        y: tag.y - 14,
        alpha: 0,
        duration: 800,
        onComplete: () => tag.destroy(),
      });
      if (s.assetSprite) {
        const sprite = s.assetSprite;
        this.scene.tweens.add({
          targets: sprite,
          alpha: 0,
          scale: 0.4,
          duration: 250,
          onComplete: () => sprite.destroy(),
        });
        s.assetSprite = null;
      }
      s.status = 'dismissed';
    }
  }

  /**
   * Scanner-route final patch. Counts toward the boss trigger and fills
   * the HUD chip. Status becomes terminal 'patched'.
   */
  private completePatch(s: HotspotState, at: { x: number; y: number }): void {
    if (!s.type || s.status !== 'exposed') return;
    const type = s.type;

    if (s.exposedCallout) {
      const c = s.exposedCallout;
      this.scene.tweens.add({
        targets: c, alpha: 0, duration: 180,
        onComplete: () => c.destroy(),
      });
      s.exposedCallout = null;
    }

    playSfx(this.scene, AUDIO_KEYS.sfxPatch);
    this.scene.add.particles(at.x, at.y, SPRITE_KEYS.pixel, {
      lifespan: 500,
      speed: { min: 80, max: 180 },
      quantity: 18,
      tint: brandHex.ok,
      alpha: { start: 1, end: 0 },
      scale: { start: 4, end: 0 },
      blendMode: 'ADD',
      emitting: false,
    }).explode(18);

    if (s.assetSprite) {
      const sprite = s.assetSprite;
      this.scene.tweens.add({
        targets: sprite,
        scale: 0,
        alpha: 0,
        duration: 250,
        ease: 'Quad.In',
        onComplete: () => sprite.destroy(),
      });
      s.assetSprite = null;
    }

    const slotIndex = this.patchedCount;
    s.status = 'patched';
    this.patchedCount++;
    this.cbs.onPatch(type, slotIndex);

    if (this.patchedCount >= config.assets.count) this.cbs.onAllPatched();
  }

  /**
   * Defensive patch on a CVE-triggered exploit. Cancels the timer, reverts
   * the hotspot to 'hidden' (asset stays there, '?' returns), and fires
   * onExploitEnd so the CVE pump can resume. No counter increment.
   */
  private defendInPlace(s: HotspotState): void {
    if (s.status !== 'exploiting') return;

    s.exploitTimer?.remove();
    s.exploitTimer = null;
    this.clearExploitBar(s);
    s.hotspot.body.clearTint();

    playSfx(this.scene, AUDIO_KEYS.sfxPatch);
    this.scene.add.particles(s.hotspot.x, s.hotspot.y, SPRITE_KEYS.pixel, {
      lifespan: 500,
      speed: { min: 80, max: 180 },
      quantity: 18,
      tint: brandHex.ok,
      alpha: { start: 1, end: 0 },
      scale: { start: 4, end: 0 },
      blendMode: 'ADD',
      emitting: false,
    }).explode(18);

    // The asset icon was revealed when the CVE hit — fade it out, then
    // re-spawn the '?' marker so the hotspot is searchable again.
    if (s.assetSprite) {
      const sprite = s.assetSprite;
      this.scene.tweens.add({
        targets: sprite,
        alpha: 0,
        duration: 200,
        onComplete: () => sprite.destroy(),
      });
      s.assetSprite = null;
    }
    s.marker = this.makeMarker(s.hotspot.x, s.hotspot.y - 28);
    s.status = 'hidden';
    this.cbs.onExploitEnd();
  }

  private markExploited(state: HotspotState): void {
    if (state.status !== 'exploiting') return;
    const type = state.type;
    state.status = 'exploited';
    state.hotspot.body.setTint(brandHex.threat);
    this.clearExploitBar(state);

    if (state.assetSprite) {
      const sprite = state.assetSprite;
      this.scene.tweens.add({
        targets: sprite, alpha: 0, duration: 250,
        onComplete: () => sprite.destroy(),
      });
      state.assetSprite = null;
    }

    this.cbs.onExploitEnd();
    if (type) this.cbs.onExploited(type);
  }

  /** Wipe every visual on a state (used by destroy()). Resets status to 'searched'. */
  private toBlank(state: HotspotState): void {
    state.exploitTimer?.remove();
    state.exploitTimer = null;
    state.scanTimer?.remove();
    state.scanTimer = null;
    state.scanTween?.stop();
    state.scanTween = null;
    this.clearExploitBar(state);
    state.hotspot.body.clearTint();
    state.marker?.destroy();
    state.marker = null;
    state.assetSprite?.destroy();
    state.assetSprite = null;
    state.carryCallout?.destroy();
    state.carryCallout = null;
    state.scanCallout?.destroy();
    state.scanCallout = null;
    state.scanBeam?.destroy();
    state.scanBeam = null;
    state.exposedCallout?.destroy();
    state.exposedCallout = null;
  }

  private clearExploitBar(state: HotspotState): void {
    state.exploitBar?.destroy();
    state.exploitBar = null;
    state.exploitBarFill?.destroy();
    state.exploitBarFill = null;
    state.exploitLabel?.destroy();
    state.exploitLabel = null;
  }

  private makeMarker(x: number, y: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y).setDepth(15);
    const ring = this.scene.add.circle(0, 0, 9, brandHex.primary, 0.18)
      .setStrokeStyle(1, brandHex.primary, 0.55);
    const glyph = this.scene.add.text(0, 0, '?', {
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '10px',
      fontStyle: '700',
      color: brand.primary,
    }).setOrigin(0.5);
    container.add([ring, glyph]);
    this.scene.tweens.add({
      targets: ring,
      scale: { from: 0.9, to: 1.25 },
      alpha: { from: 0.18, to: 0.42 },
      yoyo: true,
      repeat: -1,
      duration: 1100,
      ease: 'Sine.InOut',
    });
    this.scene.tweens.add({
      targets: container,
      y: y - 3,
      yoyo: true,
      repeat: -1,
      duration: 900,
      ease: 'Sine.InOut',
    });
    return container;
  }
}
